import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, Check, ExternalLink, Search, Users } from 'lucide-react';
import {
  useAdoptCreator,
  useExploreAccess,
  useExploreSearch,
  useJoinCreatorPool,
} from '../../hooks/useExplore';
import { getApiErrorMessage } from '../../lib/apiError';
import FreshnessBadge from '../../components/explore/FreshnessBadge';
import PoolOptInGate from '../../components/explore/PoolOptInGate';
import type { ExploreCreator, ExploreSort } from '../../types/explore';

/**
 * Agency → Explore.
 *
 * The page that makes the shared pool worth joining. Everything else in Phase 2 is plumbing that
 * feeds this one screen.
 *
 * Two things it must never do: present a pooled number as if it were the agency's own, and present
 * a stale number without saying so. Both would be a small lie that costs an agency a pitch, and
 * they would be right to blame us for it.
 */

const PLATFORMS = [
  { value: '', label: 'All platforms' },
  { value: 'TIKTOK', label: 'TikTok' },
  { value: 'INSTAGRAM', label: 'Instagram' },
  { value: 'YOUTUBE', label: 'YouTube' },
  { value: 'PINTEREST', label: 'Pinterest' },
] as const;

const SORTS: Array<{ value: ExploreSort; label: string }> = [
  { value: 'followers', label: 'Followers' },
  { value: 'engagementRate', label: 'Engagement' },
  { value: 'growth', label: 'Growth (30d)' },
  { value: 'freshness', label: 'Recently updated' },
];

function formatCount(n: number | null): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

/**
 * Engagement, with its basis spelled out.
 *
 * A TikTok lifetime-hearts proxy and a per-post rate are different quantities. Rendering both as a
 * bare percentage invites someone to compare them, which is exactly the mistake the server went to
 * the trouble of recording `engagementBasis` to prevent.
 */
function EngagementCell({ creator }: { creator: ExploreCreator }) {
  if (creator.engagementRate == null) {
    return (
      <span
        className="text-glass-text-3"
        title="Not available from this platform's profile page. A creator who connects their own account can supply it."
      >
        —
      </span>
    );
  }

  const isProxy = creator.engagementBasis === 'lifetime_hearts';
  return (
    <span
      title={
        isProxy
          ? 'Lifetime likes per video over followers — an approximation, not a per-post rate. Not comparable with Instagram or YouTube engagement.'
          : 'Average engagement per post.'
      }
    >
      {creator.engagementRate.toFixed(1)}%
      {isProxy && <span className="ml-1 text-[0.65rem] text-glass-text-3">approx</span>}
    </span>
  );
}

/**
 * The creator's picture, with the placeholder standing in when there isn't one that loads.
 *
 * Platform avatar URLs are signed and they expire, so a picture captured months ago eventually
 * stops resolving. Without `onError` the card renders a broken-image glyph, which reads as "this
 * product is broken" rather than "this picture aged out" — the neutral placeholder is the same
 * thing a creator with no captured avatar already shows.
 */
function CreatorAvatar({ src, alt }: { src: string | null; alt: string }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-glass-3">
        <Users className="h-5 w-5 text-glass-text-3" aria-hidden="true" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="h-11 w-11 rounded-full object-cover"
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

function CreatorCard({
  creator,
  onAdopt,
  adopting,
}: {
  creator: ExploreCreator;
  onAdopt: (id: string) => void;
  adopting: boolean;
}) {
  const growth = creator.followersGrowthPct30d;

  return (
    <article className="rounded-lg border border-glass bg-glass-1 p-4">
      <div className="flex items-start gap-3">
        <CreatorAvatar src={creator.avatarUrl} alt="" />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="truncate font-medium text-glass-text">
              {creator.displayName || creator.handle}
            </h3>
            {creator.verified && (
              <BadgeCheck
                className="h-4 w-4 shrink-0 text-[rgb(var(--info))]"
                aria-label="Verified on platform"
              />
            )}
          </div>
          <a
            href={creator.profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-glass-text-3 hover:text-glass-text"
          >
            @{creator.handle}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        </div>
      </div>

      {/* The creator's own description of themselves, which is usually the fastest way to tell
          whether they are worth a pitch at all — a follower count cannot say "Artist" or "KL
          based". Clamped rather than truncated server-side, so the full text is still in the
          tooltip and in the API response. */}
      {creator.bio && (
        <p className="mt-3 line-clamp-2 text-xs text-glass-text-2" title={creator.bio}>
          {creator.bio}
        </p>
      )}

      <dl className="mt-3 grid grid-cols-3 gap-2 text-sm">
        <div>
          <dt className="text-[0.7rem] uppercase tracking-wide text-glass-text-3">Followers</dt>
          <dd className="text-glass-text">{formatCount(creator.followers)}</dd>
        </div>
        <div>
          <dt className="text-[0.7rem] uppercase tracking-wide text-glass-text-3">Engagement</dt>
          <dd className="text-glass-text">
            <EngagementCell creator={creator} />
          </dd>
        </div>
        <div>
          <dt className="text-[0.7rem] uppercase tracking-wide text-glass-text-3">30d growth</dt>
          <dd className={growth != null && growth > 0 ? 'text-[rgb(var(--pos))]' : 'text-glass-text'}>
            {growth == null ? '—' : `${growth > 0 ? '+' : ''}${growth.toFixed(1)}%`}
          </dd>
        </div>
      </dl>

      <div className="mt-3 flex items-center justify-between gap-2">
        <FreshnessBadge freshness={creator.freshness} capturedAt={creator.metricsCapturedAt} />

        {creator.inRoster ? (
          <Link
            to={creator.kolProfileId ? `/agency/kols/${creator.kolProfileId}` : '/agency/kols'}
            className="inline-flex items-center gap-1 text-xs text-[rgb(var(--pos))]"
          >
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            On your roster
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => onAdopt(creator.id)}
            disabled={adopting}
            className="rounded-md border border-glass px-3 py-1 text-xs text-glass-text hover:bg-glass-2 disabled:opacity-60"
          >
            {adopting ? 'Adding…' : 'Add to roster'}
          </button>
        )}
      </div>

      <p className="mt-2 text-[0.65rem] text-glass-text-3">
        {/* Provenance, unmissable. An agency must always be able to tell a pooled number from one
            their own team recorded. */}
        Shared pool · {creator.metricsSource?.toLowerCase().replace(/_/g, ' ') ?? 'unknown source'} ·{' '}
        {/* On every card, not tucked into a settings page. The creator whose public numbers these
            are did not sign up for this, and the person most likely to be asked "where did you get
            that?" is the agency looking at this card. They should have the answer to hand. */}
        <Link to="/creator-data" target="_blank" className="underline hover:text-glass-text">
          creator data notice
        </Link>
      </p>
    </article>
  );
}

export default function Explore() {
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [platform, setPlatform] = useState('');
  const [minFollowers, setMinFollowers] = useState('');
  const [sort, setSort] = useState<ExploreSort>('followers');
  const [adoptingId, setAdoptingId] = useState<string | null>(null);

  const access = useExploreAccess();
  const join = useJoinCreatorPool();
  const adopt = useAdoptCreator();

  const params = useMemo(
    () => ({
      q: submitted || undefined,
      platform: platform || undefined,
      minFollowers: minFollowers ? Number(minFollowers) : undefined,
      sort,
    }),
    [submitted, platform, minFollowers, sort]
  );

  const canRead = access.data?.canRead ?? false;
  const search = useExploreSearch(params, canRead);
  const creators = search.data?.pages.flatMap((p) => p.creators) ?? [];

  if (access.isLoading) {
    return <p className="p-6 text-sm text-glass-text-3">Loading…</p>;
  }

  if (access.isError) {
    return (
      <p className="p-6 text-sm text-[rgb(var(--danger))]" role="alert">
        {getApiErrorMessage(access.error)}
      </p>
    );
  }

  if (access.data && !access.data.canRead) {
    return (
      <div className="p-6">
        <PoolOptInGate
          access={access.data}
          onJoin={() => join.mutate()}
          joining={join.isPending}
          error={join.isError ? getApiErrorMessage(join.error) : null}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5 p-6">
      <header>
        <h1 className="text-xl font-semibold text-glass-text">Explore creators</h1>
        <p className="text-sm text-glass-text-3">
          Public profile metrics shared by agencies on kol.my. Always check a creator&apos;s live
          profile before you pitch.
        </p>
      </header>

      {access.data && !access.data.isPlatformAdmin && (
        <p className="text-xs text-glass-text-3">
          {access.data.quota === null
            ? 'Unlimited reads during your first 30 days.'
            : `${access.data.used.toLocaleString()} of ${access.data.quota.toLocaleString()} reads used this month. Every capture your team makes adds ten.`}
        </p>
      )}

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(query.trim());
        }}
      >
        <div className="min-w-[200px] flex-1">
          <label htmlFor="explore-q" className="block text-xs text-glass-text-3">
            Search handle or name
          </label>
          <input
            id="explore-q"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ainacooks"
            className="mt-1 w-full rounded-md border border-glass bg-glass-1 px-3 py-2 text-sm text-glass-text"
          />
        </div>

        <div>
          <label htmlFor="explore-platform" className="block text-xs text-glass-text-3">
            Platform
          </label>
          <select
            id="explore-platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="mt-1 rounded-md border border-glass bg-glass-1 px-3 py-2 text-sm text-glass-text"
          >
            {PLATFORMS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="explore-min" className="block text-xs text-glass-text-3">
            Min followers
          </label>
          <input
            id="explore-min"
            type="number"
            min={0}
            value={minFollowers}
            onChange={(e) => setMinFollowers(e.target.value)}
            placeholder="10000"
            className="mt-1 w-32 rounded-md border border-glass bg-glass-1 px-3 py-2 text-sm text-glass-text"
          />
        </div>

        <div>
          <label htmlFor="explore-sort" className="block text-xs text-glass-text-3">
            Sort by
          </label>
          <select
            id="explore-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value as ExploreSort)}
            className="mt-1 rounded-md border border-glass bg-glass-1 px-3 py-2 text-sm text-glass-text"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          className="inline-flex items-center gap-1.5 rounded-md bg-accent2 px-4 py-2 text-sm font-medium text-white"
        >
          <Search className="h-4 w-4" aria-hidden="true" />
          Search
        </button>
      </form>

      {adopt.isError && (
        <p className="text-sm text-[rgb(var(--danger))]" role="alert">
          {getApiErrorMessage(adopt.error)}
        </p>
      )}

      {search.isError && (
        <p className="text-sm text-[rgb(var(--danger))]" role="alert">
          {getApiErrorMessage(search.error)}
        </p>
      )}

      {search.isLoading && <p className="text-sm text-glass-text-3">Searching…</p>}

      {!search.isLoading && creators.length === 0 && !search.isError && (
        <div className="rounded-lg border border-glass bg-glass-1 p-8 text-center">
          <p className="text-sm text-glass-text-2">No creators match that search yet.</p>
          <p className="mt-2 text-xs text-glass-text-3">
            The pool grows as agencies capture creators with the browser extension. If your team
            browses a profile with it installed, it will show up here.
          </p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {creators.map((creator) => (
          <CreatorCard
            key={creator.id}
            creator={creator}
            adopting={adopt.isPending && adoptingId === creator.id}
            onAdopt={(id) => {
              setAdoptingId(id);
              adopt.mutate(id);
            }}
          />
        ))}
      </div>

      {search.hasNextPage && (
        <div className="text-center">
          <button
            type="button"
            onClick={() => void search.fetchNextPage()}
            disabled={search.isFetchingNextPage}
            className="rounded-md border border-glass px-4 py-2 text-sm text-glass-text hover:bg-glass-2 disabled:opacity-60"
          >
            {search.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
